// Creates a view with notebook manual on the left and calculator on the right
import * as React from "react";
import { useSelector } from "react-redux";

import { FullscreenModal } from "@learningblocks/storybook-components/lib/FullscreenModal";

import { NotebookManual } from "../../NotebookManual";
import { NotebookManualProps } from "../../NotebookManual/types";
import { Calculator } from "../../Calculator";
import { SortedItem } from "../../MicroscopeSorter";
import * as styles from "./NotebookCalculatorModal.css";

interface NotebookCalculatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  notebookCollapsed: boolean;
  data: {
    notebookData: NotebookManualProps;
  };
}

// Type definitions for notebook component data structure
interface TableCell {
  type: "TITLE" | "FIXED" | "INPUT";
  text: string;
}

type TableRow = TableCell[];

type TableData = TableRow[];

interface TableQuestionComponent {
  id: string;
  type: "TABLE_QUESTION";
  data: TableData;
  [key: string]: any;
}

// Constants for data structure indices
const NOTEBOOK_CONSTANTS = {
  FIRST_PAGE_INDEX: 0,
  TABLE_COMPONENT_INDEX: 1,
  PARENTAL_ROW_INDEX: 1,
  RECOMBINANT_ROW_INDEX: 2,
  RESULT_CELL_INDEX: 1,
  RESULT_TOTAL_ROW_INDEX: 3,
} as const;

// Type guard to check if component is a DataTable
function isTableQuestionComponent(
  component: any
): component is TableQuestionComponent {
  return (
    component &&
    typeof component === "object" &&
    component.type === "TABLE_QUESTION" &&
    Array.isArray(component.data)
  );
}

// Safely update table cell text
function updateTableCell(
  data: TableData,
  rowIndex: number,
  cellIndex: number,
  newText: string
): boolean {
  if (
    data[rowIndex] &&
    data[rowIndex][cellIndex] &&
    typeof data[rowIndex][cellIndex].text === "string"
  ) {
    data[rowIndex][cellIndex].text = newText;
    return true;
  }
  return false;
}

export function NotebookCalculatorModal(props: NotebookCalculatorModalProps) {
  let sortedItems: SortedItem[] = [];

  try {
    sortedItems = useSelector(
      (state: any) => state.microscopeSorter?.sortedItems || []
    );
  } catch (e) {
    sortedItems = [];
  }

  // Get table question state for meiosis progressive revelation
  const tableQuestionState = useSelector((state: any) => {
    return state.tableQuestion?.["501"];
  });

  // Calculate counts from sorted items (will be 0 if Redux not available)
  const parentalCount = React.useMemo(() => {
    return sortedItems.filter((item) => item.selectedOption === "parental")
      .length;
  }, [sortedItems]);

  const recombinantCount = React.useMemo(() => {
    return sortedItems.filter((item) => item.selectedOption === "recombinant")
      .length;
  }, [sortedItems]);

  // Calculate which cells should be visible based on submissions (meiosis only)
  const cellVisibility = React.useMemo((): Record<string, boolean> | null => {
    if (!tableQuestionState) {
      return null; // Show all for non-meiosis sims
    }

    const cellValues = tableQuestionState.cellValues || {};

    return {
      // Row 1 input (1_3): Always visible initially
      "1_3": true,
      // Row 2 input (2_3): Visible after row 1 submitted
      "2_3": Boolean(cellValues["1_3"]?.isSubmitted),
      // Row 3 input (3_3): Visible after row 2 submitted
      "3_3": Boolean(cellValues["2_3"]?.isSubmitted),
      // Row 4 input (4_1): Visible after row 3 submitted
      "4_1": Boolean(cellValues["3_3"]?.isSubmitted),
      // Row 5 input (5_1): Visible after row 4 submitted
      "5_1": Boolean(cellValues["4_1"]?.isSubmitted),
    };
  }, [tableQuestionState]);

  // Calculate visible rows based on submissions (meiosis only)
  const visibleRowCount = React.useMemo(() => {
    if (!tableQuestionState) {
      return null; // Show all rows for non-meiosis sims
    }

    const cellValues = tableQuestionState.cellValues || {};

    // Start with header row (index 0) + first data row (index 1) + second data row (index 2)
    let visibleRows = 3;

    // Row 3: Show if row 2 input (2_3) is filled and submitted
    if (cellValues["2_3"]?.isSubmitted) {
      visibleRows = 4;
    }

    // Row 4: Show if row 3 input (3_3) is filled and submitted
    if (cellValues["3_3"]?.isSubmitted) {
      visibleRows = 5;
    }

    // Row 5: Show if row 4 input (4_1) is filled and submitted
    if (cellValues["4_1"]?.isSubmitted) {
      visibleRows = 6;
    }

    // Row 6: Show if row 5 input (5_1) is filled and submitted
    if (cellValues["5_1"]?.isSubmitted) {
      visibleRows = 7;
    }

    return visibleRows;
  }, [tableQuestionState]);

  // Create updated notebook data with microscope sorter counts
  const updatedNotebookData = React.useMemo(() => {
    const notebookData = { ...props.data.notebookData };

    // Validate structure exists
    const firstPage = notebookData.pages?.[NOTEBOOK_CONSTANTS.FIRST_PAGE_INDEX];

    if (
      !notebookData.pages ||
      !firstPage ||
      !firstPage.components ||
      !firstPage.components[NOTEBOOK_CONSTANTS.TABLE_COMPONENT_INDEX]
    ) {
      console.warn(
        "NotebookCalculatorModal: Expected notebook structure not found"
      );
      return notebookData;
    }

    const tableComponent =
      firstPage.components[NOTEBOOK_CONSTANTS.TABLE_COMPONENT_INDEX];

    // Verify it's a data table component
    if (!isTableQuestionComponent(tableComponent)) {
      console.warn(
        "NotebookCalculatorModal: Component at index is not a DATA_TABLE"
      );
      return notebookData;
    }

    // Deep clone to avoid mutation using structuredClone
    const pages = structuredClone(notebookData.pages);
    const clonedFirstPage = pages[NOTEBOOK_CONSTANTS.FIRST_PAGE_INDEX];

    if (!clonedFirstPage?.components) {
      return notebookData;
    }

    const clonedTableComponent =
      clonedFirstPage.components[NOTEBOOK_CONSTANTS.TABLE_COMPONENT_INDEX];

    if (!isTableQuestionComponent(clonedTableComponent)) {
      return notebookData;
    }

    // Update parental count in "Your data" column
    const parentalUpdated = updateTableCell(
      clonedTableComponent.data,
      NOTEBOOK_CONSTANTS.PARENTAL_ROW_INDEX,
      NOTEBOOK_CONSTANTS.RESULT_CELL_INDEX,
      String(parentalCount)
    );

    // Update recombinant count in "Your data" column
    const recombinantUpdated = updateTableCell(
      clonedTableComponent.data,
      NOTEBOOK_CONSTANTS.RECOMBINANT_ROW_INDEX,
      NOTEBOOK_CONSTANTS.RESULT_CELL_INDEX,
      String(recombinantCount)
    );

    const totalCount = updateTableCell(
      clonedTableComponent.data,
      NOTEBOOK_CONSTANTS.RESULT_TOTAL_ROW_INDEX,
      NOTEBOOK_CONSTANTS.RESULT_CELL_INDEX,
      String(parentalCount + recombinantCount)
    );

    if (!parentalUpdated || !recombinantUpdated || totalCount) {
      console.warn(
        "NotebookCalculatorModal: Could not update microscope sorter counts"
      );
    }

    // Progressive row revelation for meiosis sim only
    if (visibleRowCount !== null && cellVisibility !== null) {
      // Enable autofocus for first visible input
      clonedTableComponent.autoFocusFirstInput = true;

      // Slice the table data to show only visible rows
      clonedTableComponent.data = clonedTableComponent.data
        .slice(0, visibleRowCount)
        .map((row: TableCell[], rowIndex: number) => {
          // For each row, filter out cells that should be hidden
          return row.filter((cell: TableCell, colIndex: number) => {
            // Check if this is an INPUT cell that should be hidden
            if (cell.type === "INPUT") {
              // Create cell key based on row and column index
              const cellKey = `${rowIndex}_${colIndex}`;

              // If this input cell should not be visible yet, filter it out
              if (cellVisibility[cellKey] === false) {
                return false;
              }
            }
            return true;
          });
        });
    }

    return { ...notebookData, pages };
  }, [
    props.data.notebookData,
    parentalCount,
    recombinantCount,
    visibleRowCount,
    cellVisibility,
  ]);

  return (
    <FullscreenModal
      className={styles.modalContainer}
      isOpen={props.isOpen}
      closeButton={true}
      notebookExpanded={!props.notebookCollapsed}
      close={props.onClose}
      backLabel={"Lab"}
    >
      <div className={styles.modalLayout}>
        <div>
          <NotebookManual
            key={`notebook-${visibleRowCount}-${JSON.stringify(cellVisibility)}`}
            {...updatedNotebookData}
          />
        </div>
        <div className={styles.calculatorContainer}>
          <Calculator />
        </div>
      </div>
    </FullscreenModal>
  );
}
